class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");
    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupResults = this.querySelector("[data-luxury-predictive-box]") || this.querySelector(".luxury-search-predictive-box");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    if (popularSearchItems && popularSearchItems.length) {
      popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
    }
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");
    if (form) {
      form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 250).bind(this)
      );
    }
    if (clear) {
      clear.addEventListener("click", this.onClearSearch.bind(this));
    }
    if (window.MinimogEvents && window.MinimogTheme) {
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(false);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onClearSearch(event) {
    event.preventDefault();
    if (this.input) {
      this.input.value = "";
    }
    this.close(true);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption || "last";

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";
    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const predictiveUrl = window.MinimogSettings?.routes?.predictive_search_url || "/search/suggest";
    const searchURL = `${predictiveUrl}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(searchURL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText || response.status);
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const sectionEl =
          doc.querySelector("#shopify-section-predictive-search") ||
          doc.querySelector("[data-search-items-wrapper]") ||
          doc.body;
        const resultsMarkup = sectionEl ? sectionEl.innerHTML : "";
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        console.error("Predictive search error:", error);
        this.toggleSpinnerLoading(false);
        this.toggleClearSearch(true);
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    const hasItems = searchItemsWrapper && searchItemsWrapper.childElementCount > 0;
    this.renderSearchQueryAndMessage(hasItems);

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const query = input ? input.value : "";

    if (searchQuery) searchQuery.textContent = query;
    if (this.predictiveSearchResults) this.predictiveSearchResults.classList.remove("m:hidden");
    if (this.searchPopupResults) this.searchPopupResults.classList.remove("m:hidden");

    if (searchMessage) {
      const resultsTitle = searchMessage.dataset?.resultsTitle || "Results for";
      searchMessage.textContent = resultsTitle;
    }
    if (moreResultIcon) {
      moreResultIcon.classList.toggle("m:hidden", results);
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    if (submit) submit.style.visibility = show ? "hidden" : "visible";
    if (loading) loading.style.visibility = show ? "visible" : "hidden";
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    if (clear) clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    this.setAttribute("open", true);
    if (this.input) this.input.setAttribute("aria-expanded", "true");
    this.isOpen = true;

    if (this.predictiveSearchResults) this.predictiveSearchResults.classList.remove("m:hidden");
    if (this.searchPopupResults) this.searchPopupResults.classList.remove("m:hidden");

    if (this.searchCount) this.searchCount.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.toggleSpinnerLoading(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    if (this.predictiveSearchResults) this.predictiveSearchResults.classList.add("m:hidden");
    if (this.searchPopupResults) this.searchPopupResults.classList.add("m:hidden");

    if (this.searchCount) this.searchCount.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    const ps = document.querySelector("predictive-search");
    this.resultsMaxHeight = ps ? window.innerHeight - ps.getBoundingClientRect().bottom : 500;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);

document.addEventListener("DOMContentLoaded", function () {
  const track = document.getElementById("luxurySearchTrack");
  const fill = document.getElementById("luxurySearchProgressFill");
  const progressTrack = document.getElementById("luxurySearchProgressTrack");
  let isTicking = false;

  function updateSearchCarouselProgress() {
    if (!track || !fill) return;
    const scrollWidth = track.scrollWidth;
    const clientWidth = track.clientWidth;
    const scrollLeft = track.scrollLeft;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 2) {
      fill.style.width = "100%";
      return;
    }

    const percent = Math.min(100, Math.max(0, ((scrollLeft + clientWidth) / scrollWidth) * 100));
    fill.style.width = percent.toFixed(2) + "%";
  }

  function requestProgressUpdate() {
    if (!isTicking) {
      window.requestAnimationFrame(function () {
        updateSearchCarouselProgress();
        isTicking = false;
      });
      isTicking = true;
    }
  }

  if (track) {
    track.addEventListener("scroll", requestProgressUpdate, { passive: true });
    window.addEventListener("resize", requestProgressUpdate);

    setTimeout(updateSearchCarouselProgress, 150);
    setTimeout(updateSearchCarouselProgress, 500);
  }

  if (progressTrack && track) {
    progressTrack.style.cursor = "pointer";
    progressTrack.addEventListener("click", function (e) {
      const rect = progressTrack.getBoundingClientRect();
      const clickRatio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollTo({ left: clickRatio * maxScroll, behavior: "smooth" });
    });
  }

  document.addEventListener("click", function (e) {
    const nextBtn = e.target.closest(".luxury-search-arrow-next");
    const prevBtn = e.target.closest(".luxury-search-arrow-prev");

    if (nextBtn && track) {
      e.preventDefault();
      const firstCard = track.querySelector(".luxury-search-card");
      const step = firstCard ? (firstCard.offsetWidth + 16) * 2 : 320;
      track.scrollBy({ left: step, behavior: "smooth" });
    }
    if (prevBtn && track) {
      e.preventDefault();
      const firstCard = track.querySelector(".luxury-search-card");
      const step = firstCard ? (firstCard.offsetWidth + 16) * 2 : 320;
      track.scrollBy({ left: -step, behavior: "smooth" });
    }

    const tagBtn = e.target.closest("[data-luxury-search-tag]");
    if (tagBtn) {
      e.preventDefault();
      const query = tagBtn.getAttribute("data-luxury-search-tag");
      const searchPopup = document.querySelector("[data-search-popup]");
      if (searchPopup) {
        const input = searchPopup.querySelector("[data-search-input]");
        if (input) {
          input.value = query;
          input.dispatchEvent(new Event("input", { bubbles: true }));
          input.focus();
        }
      }
    }
  });

  if (window.MinimogEvents && window.MinimogTheme) {
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.openSearchPopup, function () {
      setTimeout(updateSearchCarouselProgress, 100);
      setTimeout(updateSearchCarouselProgress, 350);
    });
  }
});
